import { flightConfig } from "./flightConfig";

/** GCC + other IF country URL segments used for international flight status (aligned with seoSupportedCountries + common IF folders). */
const EXTRA_INTL_FLIGHT_STATUS_COUNTRIES = [
  "canada",
  "singapore",
  "australia",
  "united_kingdom",
];

let _cachedIntlSlugs = null;

/**
 * Ordered unique list of country slug segments allowed in
 * /international-flights/{country}/… flight-status URLs.
 */
export function getIntlFlightStatusCountrySlugs() {
  if (_cachedIntlSlugs) return _cachedIntlSlugs;
  const fromConfig = flightConfig.seoSupportedCountries.split(",").map((s) => s.trim()).filter(Boolean);
  _cachedIntlSlugs = [...new Set([...fromConfig, ...EXTRA_INTL_FLIGHT_STATUS_COUNTRIES])].sort();
  return _cachedIntlSlugs;
}

export function isValidIntlFlightStatusCountrySlug(slug) {
  if (!slug || typeof slug !== "string") return false;
  return getIntlFlightStatusCountrySlugs().includes(slug.trim());
}

/**
 * Max flight codes per FIS sitemap page so domestic + (codes × intl countries) stays under 50k URLs.
 */
export function getFisSitemapCodesPerPage() {
  const n = getIntlFlightStatusCountrySlugs().length;
  return Math.max(1000, Math.floor(50000 / (1 + n)));
}

/**
 * Display name for SEO title/description (e.g. united_arab_emirates → United Arab Emirates).
 */
export function humanizeCountryFolderSlug(slug) {
  if (!slug || typeof slug !== "string") return "";
  return slug
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Relative path for flight status (leading slash, no domain).
 * @param {Object} opts
 * @param {boolean} opts.isIntl
 * @param {string} [opts.countryFolder] — when set with isIntl, builds 3-segment intl URL
 * @param {string} opts.airlineUrlPhrase
 * @param {string} opts.flightCode — e.g. 6e_6089
 */
export function buildFlightStatusPath({ isIntl, countryFolder, airlineUrlPhrase, flightCode }) {
  const airSeg = encodeURIComponent(airlineUrlPhrase);
  const codeSeg = encodeURIComponent(flightCode);
  const fileName = `${codeSeg}-flight-status.html`;
  if (isIntl && countryFolder && String(countryFolder).trim()) {
    return `/international-flights/${encodeURIComponent(String(countryFolder).trim())}/${airSeg}/${fileName}`;
  }
  if (isIntl) {
    return `/international-flights/${airSeg}/${fileName}`;
  }
  return `/flights/${airSeg}/${fileName}`;
}

const MMT_FLIGHT_STATUS_SITEMAP_ORIGIN_DEFAULT = "https://www.makemytrip.com";
const MMT_FLIGHT_STATUS_SITEMAP_ORIGIN_UAE = "https://ae.makemytrip.global";

/**
 * Canonical MMT origin for FIS sitemap <loc> URLs by international country folder slug.
 * UAE routes use ae.makemytrip.global (aligned with utils/redirection.js).
 */
export function getFlightStatusSitemapOrigin(countryFolderSlug) {
  if (countryFolderSlug === "united_arab_emirates") {
    return MMT_FLIGHT_STATUS_SITEMAP_ORIGIN_UAE;
  }
  return MMT_FLIGHT_STATUS_SITEMAP_ORIGIN_DEFAULT;
}
